import { useCallback } from 'react';

import {
  ArrowRightIcon,
  Clock8Icon,
  CoinsIcon,
  ExternalLinkIcon,
  FlaskConicalIcon,
  InfoIcon,
  LightbulbIcon,
  HistoryIcon,
  PlusIcon,
  SettingsIcon,
  ChevronRightIcon,
  SparklesIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router';
import { useTitle } from 'react-use';

import { FlagEmoji } from '@/components/atoms/FlagEmoji/FlagEmoji';
import IfIcon from '@/components/atoms/Icons/IfIcon';
import { McpIcon } from '@/components/atoms/Icons/McpIcon';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ListTooltipContent } from '@/components/atoms/ListTooltipContent/ListTooltipContent';
import { StickyScrollFade } from '@/components/atoms/StickyScrollFade/StickyScrollFade';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { renderDialogIcon } from '@/components/organisms/SubNav/TreeView/Item';
import { Button } from '@/components/ui/button';
import { Card, CardHeader } from '@/components/ui/card';
import { ShortcutHint } from '@/components/ui/kbd';
import { Label } from '@/components/ui/label';
import { SHORTCUTS } from '@/constants/shortcuts';
import { useAllRules } from '@/hooks/useAllRules';
import useBrains from '@/hooks/useBrains';
import { useDeskId } from '@/hooks/useDeskId';
import useDialogs from '@/hooks/useDialogs';
import { useScrollFade } from '@/hooks/useScrollFade';
import { PendingImprovementsBadge } from '@/modules/improvements/components/PendingImprovementsBadge';
import { usePendingImprovementsCount } from '@/modules/improvements/hooks/useImprovements';
import { NEW_RULE_ID } from '@/modules/rules/constants';
import { requestNodeSelection } from '@/redux/nodes/actions';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import useSegments from '@/segment/useSegments';
import { languageAndFlagByCountryCode } from '@/shared/languageCodes';
import { PageName } from '@/shared/types/segment';
import { timeFromNow } from '@/util/dateUtils';
import { emojiFlagToCountryCode } from '@/util/emojiFlagToCountryCode';

import { OverviewActivity } from '../components/Overview/OverviewActivity';
import { OverviewFlow } from '../components/Overview/OverviewFlow';
import { OverviewPerformance } from '../components/Overview/OverviewPerformance';
import { getAIAgentType } from '../helper';

export const Overview = () => {
  useSegments(PageName.AI_AGENT_OVERVIEW);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const brainId = useSelector(selectBrainId);
  const slug = useSelector(selectAccountSlug);
  const deskId = useDeskId();
  const { brain } = useBrains(brainId);
  const { detailedOptions } = useDialogs(brainId);
  const pendingImprovementsCount = usePendingImprovementsCount(brainId ?? undefined);

  const { language, last_trained, brain_type, name, starting_node_id } = brain || {};

  // Resolve the configured starting node (if any) to its label + dialog for the deeplink.
  // Reuses the same detailedOptions node lookup as the Reminders/Settings dropdowns.
  const startingNode = starting_node_id
    ? detailedOptions.find((option) => option.value === starting_node_id)
    : undefined;

  const aiAgentType = brain_type ? getAIAgentType(brain_type) : '';
  const countryCode = language
    ? emojiFlagToCountryCode(languageAndFlagByCountryCode[language]?.flag)
    : undefined;
  const languageName = language ? (languageAndFlagByCountryCode[language]?.name ?? language) : '';

  // Fetch all rules connected to this brain
  const { rulesConnected, rulesWithDeskName } = useAllRules({ brainId });
  const rulesCount = rulesConnected?.length ?? 0;
  const ruleHref = (ruleDeskId: string, ruleId: string) =>
    `/${slug}/environments/${ruleDeskId}/rules/${ruleId}`;
  const singleRule = rulesCount === 1 ? rulesWithDeskName?.[0] : undefined;

  const handleCreateRule = () => {
    navigate(`/${slug}/environments/${deskId}/rules/${NEW_RULE_ID}?brainId=${brainId}`);
  };

  useTitle(t('pages.ai_agent_overview', { 0: name ?? '-' }));

  const { scrollRef, showFade, onScroll } = useScrollFade<HTMLDivElement>();

  const handleLeftWheel = useCallback(
    (e: React.WheelEvent) => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop += e.deltaY;
      }
    },
    [scrollRef]
  );

  // Opens the starting node's dialog and preselects the node so its editor is already open.
  const handleStartingNodeClick = () => {
    if (!startingNode) {
      return;
    }
    navigate(`/${slug}/ai-agents/${brainId}/workflows/dialogs/${startingNode.dialog_id}`);
    dispatch(requestNodeSelection(startingNode.value));
  };

  const startingDialogTooltip = (
    <div className="flex w-70 flex-col">
      <div className="border-b-[0.5px] border-b-muted/40 px-3 pt-2.5 pb-3 text-xs leading-5 text-primary-foreground/80">
        {t('ai_agents.conversation_start.helper_text')}
      </div>
      <div className="px-3 pt-3 pb-3.5">
        <button
          type="button"
          onClick={() => navigate(`/${slug}/ai-agents/${brainId}/settings#conversation-start`)}
          className="inline-flex items-center gap-2 text-sm leading-none font-medium"
        >
          {t('ai_agents.conversation_start.configure')}
          <ExternalLinkIcon size={16} />
        </button>
      </div>
    </div>
  );

  return (
    <div className="mx-auto flex w-full max-w-(--layout-max-width) flex-col gap-4 px-5 pt-4 lg:h-full lg:flex-row">
      {/* Left Sidebar Card */}
      <div className="order-2 flex w-[348px] flex-col gap-3 lg:order-1" onWheel={handleLeftWheel}>
        <Card className="rounded-md border-border bg-background p-0 text-foreground shadow-none">
          <CardHeader className="items-end gap-0 space-y-5 p-5 pb-6">
            {/* Agent Name and Version */}
            <div className="flex w-full items-center gap-1">
              <div className="flex items-center gap-1">
                <span className="text-base leading-6 font-semibold">{name}</span>
              </div>
            </div>

            {/* AI Agent Type */}
            <div className="flex w-full flex-col gap-0">
              <span className="text-xs font-medium text-muted-foreground">
                {t('ai_agents.overview.ai_agent_type')}
              </span>
              <div className="flex items-center gap-1">
                <CoinsIcon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm leading-5 font-normal">{aiAgentType}</span>
              </div>
            </div>

            {/* Language */}
            <div className="flex w-full flex-col gap-0">
              <span className="text-xs font-medium text-muted-foreground">
                {t('field.language')}
              </span>
              <div className="flex items-center gap-1">
                {countryCode && <FlagEmoji countryCode={countryCode} />}
                <span className="text-sm leading-5 font-normal">{languageName}</span>
              </div>
            </div>

            {/* Last Trained */}
            <div className="flex w-full flex-col gap-0">
              <span className="text-xs font-medium text-muted-foreground">
                {t('brains.last_trained')}
              </span>
              <div className="flex items-center gap-1">
                <Clock8Icon className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm leading-5 font-normal">
                  {last_trained ? (
                    <TimestampTooltip timestamp={last_trained}>
                      {timeFromNow(last_trained)}
                    </TimestampTooltip>
                  ) : (
                    '-'
                  )}
                </span>
              </div>
            </div>

            {/* Conversation start (starting node) */}
            {startingNode && (
              <div className="flex w-full flex-col gap-0">
                <span className="flex items-center gap-1 text-xs font-medium text-muted-foreground">
                  {t('ai_agents.conversation_start.overview_label')}
                  <InfoTooltip
                    variant="rich"
                    size="auto"
                    side="right"
                    delayDuration={200}
                    content={startingDialogTooltip}
                  >
                    <InfoIcon size={14} className="cursor-pointer text-muted-foreground" />
                  </InfoTooltip>
                </span>
                <div className="group flex w-fit items-center gap-1">
                  <span className="text-muted-foreground">
                    {renderDialogIcon(startingNode.node_type)}
                  </span>
                  <Button variant="link" size="flush" onClick={handleStartingNodeClick}>
                    {startingNode.label}
                  </Button>
                  <ArrowRightIcon className="h-4 w-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                </div>
              </div>
            )}

            {/* Rules */}
            <div className="flex w-full max-w-[308px] flex-col gap-1">
              <Label className="text-xs font-medium text-muted-foreground">
                {t('common.rules')}
              </Label>
              {rulesCount === 0 ? (
                <Button
                  type="button"
                  variant="link-secondary"
                  className="h-auto justify-start gap-2 px-0 py-0 font-medium text-primary hover:bg-transparent"
                  onClick={handleCreateRule}
                >
                  <PlusIcon className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{t('common.create')}</span>
                </Button>
              ) : singleRule ? (
                <div className="flex w-full min-w-0 items-center gap-1">
                  <IfIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <Link
                    to={ruleHref(singleRule.desk_id, singleRule.rule_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex min-w-0 cursor-pointer items-center gap-1 text-sm leading-5 font-normal text-foreground hover:text-primary hover:underline"
                  >
                    <span className="truncate">{singleRule.name}</span>
                    <ExternalLinkIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </Link>
                </div>
              ) : (
                <InfoTooltip
                  content={
                    <ListTooltipContent
                      title={t('common.rules')}
                      items={
                        rulesWithDeskName?.map((rule) => ({
                          id: rule.rule_id,
                          label: rule.name,
                          href: ruleHref(rule.desk_id, rule.rule_id),
                        })) || []
                      }
                    />
                  }
                  variant="rich"
                  side="right"
                  align="center"
                  size="auto"
                >
                  <div className="flex w-fit cursor-pointer items-center gap-1">
                    <IfIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="text-sm leading-5 font-normal">
                      {`${rulesCount} ${t('common.rules')}`}
                    </span>
                  </div>
                </InfoTooltip>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Navigation Items Below Card */}
        <div className="flex flex-col gap-1 px-3">
          <Button
            variant="ghost-neutral"
            className="group h-9 w-full justify-start gap-2 rounded bg-transparent px-2 py-1.5"
            onClick={() => navigate(`/${slug}/ai-agents/${brainId}/mcps`)}
          >
            <McpIcon />
            <span className="flex-1 text-left text-sm">{t('common.mcps')}</span>
            <ShortcutHint
              shortcut={SHORTCUTS.goToMcps}
              className="group-hover:hidden"
              kbdClassName="bg-background"
            />
            <ChevronRightIcon className="mr-0.5 hidden h-4 w-4 text-muted-foreground group-hover:block" />
          </Button>

          <Button
            variant="ghost-neutral"
            className="group h-9 w-full justify-start gap-2 rounded bg-transparent px-2 py-1.5"
            onClick={() => navigate(`/${slug}/ai-agents/${brainId}/insights`)}
          >
            <LightbulbIcon />
            <span className="flex-1 text-left text-sm">{t('common.insights')}</span>
            <ShortcutHint
              shortcut={SHORTCUTS.goToInsights}
              className="group-hover:hidden"
              kbdClassName="bg-background"
            />
            <ChevronRightIcon className="mr-0.5 hidden h-4 w-4 text-muted-foreground group-hover:block" />
          </Button>

          <Button
            variant="ghost-neutral"
            className="group h-9 w-full justify-start gap-2 rounded bg-transparent px-2 py-1.5"
            onClick={() => navigate(`/${slug}/ai-agents/${brainId}/simulations`)}
          >
            <FlaskConicalIcon size={16} />
            <span className="flex-1 text-left text-sm">{t('simulations.title')}</span>
            <ShortcutHint
              shortcut={SHORTCUTS.goToSimulations}
              className="group-hover:hidden"
              kbdClassName="bg-background"
            />
            <ChevronRightIcon className="mr-0.5 hidden h-4 w-4 text-muted-foreground group-hover:block" />
          </Button>

          <Button
            variant="ghost-neutral"
            className="group h-9 w-full justify-start gap-2 rounded bg-transparent px-2 py-1.5"
            onClick={() => navigate(`/${slug}/ai-agents/${brainId}/improvements`)}
          >
            <SparklesIcon size={16} />
            <span className="flex-1 text-left text-sm">{t('improvements.title')}</span>
            <PendingImprovementsBadge
              count={pendingImprovementsCount}
              className="group-hover:hidden"
            />
            <ShortcutHint
              shortcut={SHORTCUTS.goToImprovements}
              className="group-hover:hidden"
              kbdClassName="bg-background"
            />
            <ChevronRightIcon className="mr-0.5 hidden h-4 w-4 text-muted-foreground group-hover:block" />
          </Button>

          <Button
            variant="ghost-neutral"
            className="group h-9 w-full justify-start gap-2 rounded bg-transparent px-2 py-1.5"
            onClick={() => navigate(`/${slug}/ai-agents/${brainId}/versions`)}
          >
            <HistoryIcon />
            <span className="flex-1 text-left text-sm">{t('common.versions')}</span>
            <ShortcutHint
              shortcut={SHORTCUTS.goToVersions}
              className="group-hover:hidden"
              kbdClassName="bg-background"
            />
            <ChevronRightIcon className="mr-0.5 hidden h-4 w-4 text-muted-foreground group-hover:block" />
          </Button>

          <Button
            variant="ghost-neutral"
            className="group h-9 w-full justify-start gap-2 rounded bg-transparent px-2 py-1.5"
            onClick={() => navigate(`/${slug}/ai-agents/${brainId}/settings`)}
          >
            <SettingsIcon />
            <span className="flex-1 text-left text-sm">{t('common.settings')}</span>
            <ShortcutHint
              shortcut={SHORTCUTS.goToSettings}
              className="group-hover:hidden"
              kbdClassName="bg-background"
            />
            <ChevronRightIcon className="mr-0.5 hidden h-4 w-4 text-muted-foreground group-hover:block" />
          </Button>
        </div>
      </div>

      {/* Right Content Area */}
      <div className="relative order-1 min-w-0 flex-1 lg:order-2 lg:overflow-hidden">
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className="flex flex-col gap-4 lg:h-full lg:overflow-y-auto lg:pb-6"
        >
          <OverviewFlow />
          <OverviewPerformance />
          <OverviewActivity />
        </div>
        <StickyScrollFade show={showFade} direction="bottom" className="bottom-0 h-16" />
      </div>
    </div>
  );
};
